import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash.debounce';

export default function AsyncMultiSelect({
  label,
  placeholder = 'Search…',
  value = [],
  onChange,
  searchFn,
  max = Infinity,
  renderItem,
}) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const acRef = useRef();

  const doSearch = useCallback(
    debounce(async (query) => {
      if (!query?.trim()) {
        setItems([]);
        return;
      }
      acRef.current?.abort();
      const controller = new AbortController();
      acRef.current = controller;
      setLoading(true);
      try {
        const res = await searchFn(query, { signal: controller.signal });
        setItems(res);
      } catch (e) {
        if (e.name !== 'AbortError' && e.name !== 'CanceledError') console.error(e);
      } finally {
        setLoading(false);
      }
    }, 250),
    [searchFn]
  );

  useEffect(() => {
    doSearch(q);
    return () => doSearch.cancel();
  }, [q, doSearch]);

  const add = (it) => {
    if (value.find((v) => v.id === it.id)) return;
    if (value.length >= max) return; // silently ignore beyond cap
    onChange([...(value || []), it]);
    setItems([]);
    setQ('');
  };

  const remove = (id) => {
    onChange((value || []).filter((v) => v.id !== id));
  };

  const remaining = Math.max(0, max - (value?.length || 0));

  return (
    <div>
      {label && <label className="block text-sm font-medium text-gray-800 mb-1">{label}</label>}
      <div className="rounded-lg border border-gray-200 p-2">
        <div className="flex flex-wrap gap-2 mb-2">
          {(value || []).map((v) => (
            <span key={v.id} className="inline-flex items-center gap-2 bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">
              {v.avatar && <img src={v.avatar} className="h-5 w-5 rounded-full object-cover" alt="" />}
              <span className="truncate max-w-[140px]">{v.label}</span>
              <button type="button" onClick={() => remove(v.id)} className="text-gray-500 hover:text-gray-700">×</button>
            </span>
          ))}
        </div>
        <div className="relative">
          <input
            type="search"
            inputMode="search"
            className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
            placeholder={remaining > 0 ? placeholder : `Max ${max} selected`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            disabled={remaining <= 0}
          />
          {loading && (
            <div className="absolute right-2 top-2 text-xs text-gray-500">Loading…</div>
          )}
          {items.length > 0 && (
            <ul className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
              {items.map((it) => (
                <li key={it.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer" onMouseDown={() => add(it)}>
                  {renderItem ? (
                    renderItem(it)
                  ) : (
                    <>
                      {it.avatar && <img src={it.avatar} className="h-6 w-6 rounded-full object-cover" alt="" />}
                      <span className="text-sm text-gray-900">{it.label}</span>
                      {it.sub && <span className="ml-auto text-xs text-gray-500">{it.sub}</span>}
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {max !== Infinity && (
        <p className="mt-1 text-xs text-gray-500">{remaining} remaining</p>
      )}
    </div>
  );
}

